import React, { useState } from 'react';
import parse from 'html-react-parser';
import { Svg } from '../../1_Atoms/Svg/Svg';
import RangeSlider from './RangeSlider/RangeSlider';
import { convertMinutesToHours } from '../../../utils/helpers';
import { languageController } from '../../../controllers/language/languageController';

interface FormExtraParams {
  introductionText?: string;
  rangeGainBy10: string | number;
  timeChargeBy10: string | number;
  disclaimerText?: string;
}

interface EVBatteryCalculatorProps {
  formExtraParams: FormExtraParams;
}

interface RangeValues {
  startValue: number;
  endValue: number;
}

const getEVBatteryDistance = (startValue: number, endValue: number, rangeGainBy10: string | number): number => {
  return ((endValue - startValue) / 10) * Number(rangeGainBy10);
};

const getEVBatteryHours = (startValue: number, endValue: number, timeChargeBy10: string | number): number => {
  return ((endValue - startValue) / 10) * Number(timeChargeBy10);
};

const EVBatteryCalculator: React.FC<EVBatteryCalculatorProps> = ({
  formExtraParams
}) => {
  const { introductionText, rangeGainBy10, timeChargeBy10, disclaimerText } = formExtraParams;
  const [rangeVal, setRangeVal] = useState<RangeValues>({
    startValue: 20,
    endValue: 80,
  });

  const { startValue, endValue } = rangeVal;

  const [resultDistance, setResultDistance] = useState<number>(() => getEVBatteryDistance(startValue, endValue, rangeGainBy10));
  const [resultHours, setResultHours] = useState<number>(() => getEVBatteryHours(startValue, endValue, timeChargeBy10));

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const numericVal = Number(value);

    if (name === 'startValue' && numericVal >= endValue) {
      return;
    } else if (name === 'endValue' && numericVal <= startValue) {
      return;
    }

    setRangeVal({ ...rangeVal, [name]: numericVal });

    let newDistance: number, newHours: number;

    if (name === 'startValue') {
      newDistance = getEVBatteryDistance(numericVal, endValue, rangeGainBy10);
      newHours = getEVBatteryHours(numericVal, endValue, timeChargeBy10);
    }
    else if (name === 'endValue') {
      newDistance = getEVBatteryDistance(startValue, numericVal, rangeGainBy10);
      newHours = getEVBatteryHours(startValue, numericVal, timeChargeBy10);
    } else {
      return;
    }

    setResultDistance(newDistance);
    setResultHours(newHours);
  };

  const generateHoursCopy = (): string => {
    const convertedTime = convertMinutesToHours(resultHours);
    return `${convertedTime?.hours || 0} ${languageController.getCopy('global', 'hours') || ''}${(convertedTime?.hours || 0) > 1 ? 's' : ''} ${convertedTime?.minutes || 0} Min`;
  };

  return (
    <div className="mz-ev-battery-calculator">
      <div className="mz-container">
        <div className="mz-full-width mz-ev-battery-calculator__inner">
          {introductionText && (
            <div className="mz-ev-battery-calculator__intro-text">
              {parse(introductionText)}
            </div>
          )}
          <RangeSlider
            startValue={startValue}
            endValue={endValue}
            onChange={handleRangeChange}
          />
          <div className="mz-ev-battery-calculator__results">
            <p className="paragraph">{parse(languageController.getCopy('EVBatteryCalculator', 'charging') || '')} <span className="strong">{startValue}% {languageController.getCopy('global', 'to') || ''} {endValue}{languageController.getCopy('global', 'percent_symbol') || ''}</span></p>
            <h5>{languageController.getCopy('global', 'adds') || ''} <span className="strong">{resultDistance.toFixed(0)}</span>{parse(languageController.getCopy('EVBatteryCalculator', 'range') || '')} <span className="strong">{generateHoursCopy()}</span></h5>
          </div>
          {
            disclaimerText && (
              <div className="mz-ev-battery-calculator__disclaimer label text--grey">
                {parse(disclaimerText)}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}

export default EVBatteryCalculator;
